import { UNIVERSAL_SEARCH_FACETS } from '@/features/shared/constants/global'
import { apiPost } from '@/features/shared/utils/dataFetching'
import { isValidZip } from '@/features/shared/utils/validator'
import { getSelectedParams, normalizeSearchResults } from '@/features/shop/services/Search/utils'
import { shoppingStoresQueryOptions } from '@/features/shop/services/ShoppingStore/queries'
import { type QueryFunctionContext } from '@tanstack/react-query'
import { type SearchResponse } from '@/features/shop/services/Search/types'
import {
  type InfiniteUniversalSearchQueryKey,
  type UniversalSearchQueryKey,
} from '@/features/shop/services/UniversalCategories/queries'

const fetchUniversalSearchProducts = async (
  context: QueryFunctionContext<UniversalSearchQueryKey>,
  contexts?: string[],
) => {
  const [, params] = context.queryKey
  const { zip = 0, contexts: contextsParam, pageParam, ...restParams } = params
  return apiPost<SearchResponse>({
    config: {
      url: '/search/v4/universal/search',
      data: {
        facets: Object.values(UNIVERSAL_SEARCH_FACETS),
        hits_per_page: 30,
        page: pageParam || 1,
        ...(isValidZip(zip) && { zip: String(zip) }),
        ...(contexts?.length && { contexts }),
        ...restParams,
      },
    },
    options: { includeAuthHeader: false },
    context,
    fetcherName: 'fetchUniversalSearchProducts',
  })
}

export const fetchUniversalSearchProductsWithZip = async (
  context: QueryFunctionContext<UniversalSearchQueryKey>,
) => {
  const [, params] = context.queryKey
  const { zip = 0, contexts: contextsParam = [], category_id } = params
  const selectedContexts = getSelectedParams(contextsParam)
  const validZip = isValidZip(zip) ? String(zip) : undefined

  // Get zip contexts if no contexts exist in URL
  let zipContexts: string[] = []
  if (!selectedContexts.length && validZip) {
    const store_locations = await context.client.query(
      shoppingStoresQueryOptions({ address_zip_code: validZip }),
    )
    const stores = store_locations.delivery_stores
    zipContexts = stores.reduce<string[]>((acc, storeInZip) => {
      acc.push(storeInZip.context_value)
      return acc
    }, [])
  }

  const contexts = selectedContexts.length ? selectedContexts : zipContexts
  const searchResponse = await fetchUniversalSearchProducts(context, contexts)
  return normalizeSearchResults(searchResponse, { category_id, ...params })
}

const fetchInfiniteUniversalSearchProducts = async (
  context: QueryFunctionContext<InfiniteUniversalSearchQueryKey, number>,
  contexts?: string[],
) => {
  const { pageParam } = context
  const [, params] = context.queryKey
  const { zip = 0, contexts: contextsParam, ...restParams } = params
  return apiPost<SearchResponse>({
    config: {
      url: '/search/v4/universal/search',
      data: {
        facets: Object.values(UNIVERSAL_SEARCH_FACETS),
        hits_per_page: 48,
        page: pageParam || 1,
        ...(isValidZip(zip) && { zip: String(zip) }),
        ...(contexts?.length && { contexts }),
        ...restParams,
      },
    },
    options: { includeAuthHeader: false },
    context,
    fetcherName: 'fetchUniversalSearchProducts',
  })
}

export const fetchInfiniteUniversalSearchProductsWithZip = async (
  context: QueryFunctionContext<InfiniteUniversalSearchQueryKey, number>,
) => {
  const [, params] = context.queryKey
  const { zip = 0, contexts: contextsParam = [], category_id } = params
  const selectedContexts = getSelectedParams(contextsParam)
  const validZip = isValidZip(zip) ? String(zip) : undefined

  // Get zip contexts if no contexts exist in URL
  let zipContexts: string[] = []
  if (!selectedContexts.length && validZip) {
    const store_locations = await context.client.query(
      shoppingStoresQueryOptions({ address_zip_code: validZip }),
    )
    const stores = store_locations.delivery_stores
    zipContexts = stores.reduce<string[]>((acc, storeInZip) => {
      acc.push(storeInZip.context_value)
      return acc
    }, [])
  }

  const contexts = selectedContexts.length ? selectedContexts : zipContexts
  const searchResponse = await fetchInfiniteUniversalSearchProducts(context, contexts)
  return normalizeSearchResults(searchResponse, { category_id, ...params })
}
